import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Database, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact admin-dashboard tile that reports the most recent Mongo→Storage
 * snapshot and lets admins fire one on demand. Polls every 5 minutes so a
 * dashboard left open shows the new state after the cron tick.
 */
function relativeTime(iso) {
  if (!iso) return 'never';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'never';
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function fmtMB(bytes) {
  if (!bytes && bytes !== 0) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(1)} KB`;
}

export default function BackupStatusWidget() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/snapshots/status`);
      if (r.ok) {
        const data = await r.json();
        setStatus(data);
      }
    } catch (err) {
      // Silently fail — widget renders an "unknown" state.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    toast.loading('Snapshotting database…', { id: 'snap' });
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/snapshots/run-now`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error('Snapshot failed');
      const data = await r.json();
      const s = data.summary || {};
      toast.success(
        `Snapshot complete · ${s.collections || 0} collections · ${(s.total_mb || 0).toFixed(1)} MB`,
        { id: 'snap', duration: 5000 }
      );
      await load();
    } catch (err) {
      toast.error('Snapshot failed — check backend logs', { id: 'snap' });
    } finally {
      setRunning(false);
    }
  };

  const latest = status?.latest;
  const total = status?.total_snapshots || 0;
  const totalCollections = Array.isArray(latest?.collections) ? latest.collections.length : 0;
  const totalBytes = latest?.total_bytes || 0;
  const completedAt = latest?.completed_at;
  const fresh = completedAt
    ? Date.now() - new Date(completedAt).getTime() < 36 * 3600 * 1000
    : false;
  const Icon = fresh ? CheckCircle2 : completedAt ? AlertCircle : Database;
  const iconColor = fresh ? 'text-emerald-400' : completedAt ? 'text-amber-400' : 'text-slate-400';

  return (
    <Card
      data-testid="backup-status-widget"
      className="bg-gradient-to-br from-slate-800/70 to-slate-900/70 border-slate-700"
    >
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className={`w-11 h-11 rounded-xl bg-slate-900/60 flex items-center justify-center ${iconColor}`}>
            <Icon className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 text-slate-200 font-semibold">
              <span>Database snapshot</span>
              {fresh && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  fresh
                </span>
              )}
            </div>
            {loading ? (
              <div className="text-xs text-slate-400 flex items-center gap-2 mt-1">
                <Loader2 className="w-3 h-3 animate-spin" /> loading…
              </div>
            ) : completedAt ? (
              <div className="text-xs text-slate-400 mt-1">
                Last backup: <span className="text-slate-200">{relativeTime(completedAt)}</span>
                {' · '}
                <span className="text-slate-200">{totalCollections}</span> collections
                {' · '}
                <span className="text-slate-200">{fmtMB(totalBytes)}</span>
                {' · '}
                <span className="text-emerald-400" title="Stored in object storage">
                  ✓ durable
                </span>
              </div>
            ) : (
              <div className="text-xs text-slate-400 mt-1">
                No snapshots yet — click "Run now" to create the first one.
              </div>
            )}
            <div className="text-[10px] text-slate-500 mt-1">
              Nightly at 02:00 UTC · {total} total snapshots stored
            </div>
          </div>

          <Button
            data-testid="run-snapshot-now-btn"
            onClick={runNow}
            disabled={running}
            size="sm"
            className="bg-slate-700 hover:bg-slate-600 text-slate-100 border border-slate-600"
          >
            {running ? (
              <>
                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Running…
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3 mr-1.5" /> Run now
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
